import * as React from 'react'
import { connect, Dispatch } from 'react-redux'
import { ApolloClient, ApolloQueryResult } from 'apollo-client'
import { NormalizedCache } from 'apollo-cache-inmemory'
import * as R from 'ramda'
import { withRouter, RouteComponentProps } from 'react-router'
import * as queryString from 'query-string'

import { Store } from '../../reducers'
import { getProjectId, getActiveSpaceType } from '../../reducers/project'
import { GET_PROJECT_QUERY, ProjectType } from '../Projects/projectQueries'
import {
  loadProject,
  loadProjectError,
  initializeProject,
  unloadProject
} from '../../actions/project'
import { Action } from '../../actions/types'
import {
  DEFAULT_VALUES_QUERY,
  DefaultValues
} from '../Workspace/Calculation/defaultValuesQuery'
import { getLocation } from '../../location_changes/conversionHelper'

type MaybeProject = ProjectType | null
type ProjectResponse = ApolloQueryResult<{ project: MaybeProject }>
type DefaultsResponse = ApolloQueryResult<{ defaultValues: DefaultValues }>

type LoaderRouteProps = RouteComponentProps<{ projectId: string }>

type LoaderProps = {
  render: (
    loading: boolean,
    error: string | null
  ) => React.ReactElement<any> | null // tslint:disable-line

  loadedProjectId: string | undefined
  activeSpaceType: string

  loadProject: (project: ProjectType) => Promise<string | null>
  unloadProject: () => Action
  loadProjectError: (error: string) => Action
  initializeProject: (defaults: DefaultValues) => Promise<string | null>
} & LoaderRouteProps

type LoaderState = {
  loading: boolean
  error: string | null
}

// tslint:disable-next-line no-any
function isGraphQlAuthError(err: any) {
  return (
    err.graphQLErrors &&
    R.find(R.propEq('name', 'AuthenticationError'), err.graphQLErrors)
  )
}

/*
  Project loader is responsible for making sure that we have a project matching the given
  URL loaded in the Redux Store. Typically, when the component is unmounted, the project
  is cleared from Redux store. The only exception is unsaved projects. We don't
  have any backing "storage" for user's inputs so we keep the redux state.

  We should look into creating a "dummy" empty project object in Apollo store which
  could hold the "new project". This way we could treat new project the same way
  everywhere.
*/

export class ProjectLoader extends React.Component<LoaderProps, LoaderState> {
  static contextTypes = {
    client: () => null
  }
  client: ApolloClient<NormalizedCache>

  // tslint:disable-next-line
  constructor(props: LoaderProps, context?: any) {
    super(props)
    this.state = { loading: false, error: null }
    this.client = context.client
  }

  componentDidMount() {
    this.loadIfNecessary(this.props)
  }

  componentWillReceiveProps(nextProps: LoaderProps) {
    if (
      !this.state.error &&
      (nextProps.match.params.projectId !== this.props.match.params.projectId ||
        nextProps.loadedProjectId !== this.props.loadedProjectId)
    ) {
      this.loadIfNecessary(nextProps)
    }
  }

  componentWillUnmount() {
    /* If we're working on a new project, we need to keep user's settings around. In a perfect
    world, this would not be something we'd have to do. */
    if (this.props.loadedProjectId !== 'new') {
      this.props.unloadProject()
    }
  }

  loadIfNecessary(props: LoaderProps) {
    const requestedId = this.props.match.params.projectId
    const queries = queryString.parse(props.location.search)

    if (!requestedId) {
      console.warn('Project ID not specified?')
      return
    }

    if (requestedId !== props.loadedProjectId) {
      if (requestedId === 'new') {
        this.initializeProject()
      } else {
        this.loadProject(requestedId, queries.caseId)
      }
    }
  }

  async projectLoaded(project: MaybeProject, caseId?: string) {
    if (!project) {
      this.props.loadProjectError('Project not found')
      this.setState({ loading: false, error: 'Project not found' })
      return
    }

    await this.props.loadProject({
      ...project,
      activeCase: caseId ? caseId : project.activeCase
    })
    this.setState(() => ({ loading: false, error: null }))
  }

  async loadProject(projectId: string, activeCaseId?: string) {
    this.setState({ loading: true, error: null })

    try {
      const response: ProjectResponse = await this.client.query<{
        project: MaybeProject
      }>({
        query: GET_PROJECT_QUERY,
        variables: { projectId: projectId }
      })
      await this.projectLoaded(response.data.project, activeCaseId)
    } catch (err) {
      /* If this is an authentication error, send user to login page */
      if (isGraphQlAuthError(err)) {
        this.props.history.push(
          `/login?redirect=${this.props.location.pathname}`
        )
        return
      }
      this.setState({ loading: false, error: err.message ? err.message : err })

      console.error('Error fetching project', err)
      this.props.loadProjectError('Project could not be loaded')
    }
  }

  async initializeProject() {
    this.setState({ loading: true, error: null })
    try {
      const response: DefaultsResponse = await this.client.query<{
        defaultValues: DefaultValues
      }>({
        query: DEFAULT_VALUES_QUERY,
        variables: {
          spaceId: this.props.activeSpaceType,
          location: getLocation()
        }
      })

      await this.props.initializeProject(response.data.defaultValues)
      this.setState({ loading: false, error: null })
    } catch (err) {
      console.error('Error fetching default values')
      this.setState({ loading: false, error: err.message ? err.message : err })
      this.props.loadProjectError('Could not load default values')
    }
  }

  render() {
    return this.props.render(this.state.loading, this.state.error)
  }
}

const mapStateToProps = (state: Store.Complete) => ({
  loadedProjectId: getProjectId(state),
  activeSpaceType: getActiveSpaceType(state)
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  loadProject: (project: ProjectType) => dispatch(loadProject(project)),
  unloadProject: () => dispatch(unloadProject()),
  loadProjectError: (error: string) => dispatch(loadProjectError(error)),
  initializeProject: (defaults: DefaultValues) =>
    dispatch(initializeProject(defaults))
})

export { withLoadedProject } from './projectLoadWrapper'

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ProjectLoader)
)



// WEBPACK FOOTER //
// ./src/Components/ProjectLoader/index.tsx